import { GLYPHS } from "./glyphs";

// ---------- lettering engine (1000 UPM, baseline y=0, caps top y=-625) ----------
const CAP = 625;
const KERN = { LL: -14, LI: -8, IN: -4, NG: -6, TS: -34, SU: -8, SE: -12, EL: -6, UN: -4, NS: -6, ET: -22, ST: -20 };

export const layout = (text, { wght = 500, tracking = 70, kern = KERN, wordGap = 230 } = {}) => {
  const set = GLYPHS[String(wght)].glyphs;
  const out = [];
  let x = 0;
  const chars = [...text];
  chars.forEach((ch, i) => {
    if (ch === " ") {
      x += wordGap;
      return;
    }
    const g = set[ch];
    out.push({ ch, x, i });
    const next = chars[i + 1];
    x += g.adv + tracking + (next && kern[ch + next] ? kern[ch + next] : 0);
  });
  return { letters: out, width: x - tracking, wght };
};

export const Glyph = ({ ch, wght = 500, x = 0, y = 0, fill = "currentColor", ...rest }) => (
  <path d={GLYPHS[String(wght)].glyphs[ch].d} transform={`translate(${x} ${y})`} fill={fill} {...rest} />
);

// Plain custom-set wordmark (used as baseline across concepts)
export const WordSet = ({ text = "SELLING SUNSETS", wght = 500, tracking = 70, renderS, fill = "currentColor" }) => {
  const { letters } = layout(text, { wght, tracking });
  return (
    <>
      {letters.map((l) =>
        l.ch === "S" && renderS ? (
          <g key={l.i} transform={`translate(${l.x} 0)`}>{renderS(l, wght)}</g>
        ) : (
          <Glyph key={l.i} ch={l.ch} wght={wght} x={l.x} fill={fill} />
        )
      )}
    </>
  );
};

export const wordWidth = (text = "SELLING SUNSETS", opts) => layout(text, opts).width;

// Convenience: an SVG wrapping a word with the right viewBox
export const WordSVG = ({ text = "SELLING SUNSETS", wght = 500, tracking = 70, renderS, className, style, pad = 40 }) => {
  const w = wordWidth(text, { wght, tracking });
  return (
    <svg viewBox={`${-pad} -${CAP + 60} ${w + pad * 2} ${CAP + 120}`} className={className} style={style} role="img" aria-label={text}>
      <WordSet text={text} wght={wght} tracking={tracking} renderS={renderS} />
    </svg>
  );
};

let uid = 0;
const nextId = (p) => `${p}-${++uid}`;

// ---------- CONCEPT A · HORIZON — the S divided by a hairline, upper half displaced ----------
export const HorizonS = ({ wght = 500, gap = 20, shift = 34, fill = "currentColor" }) => {
  const id = nextId("hz");
  const mid = -CAP / 2;
  return (
    <>
      <defs>
        <clipPath id={`${id}-t`}><rect x="-200" y="-800" width="1200" height={800 + mid - gap / 2} /></clipPath>
        <clipPath id={`${id}-b`}><rect x="-200" y={mid + gap / 2} width="1200" height="900" /></clipPath>
      </defs>
      <g clipPath={`url(#${id}-t)`} transform={`translate(${shift} 0)`}><Glyph ch="S" wght={wght} fill={fill} /></g>
      <g clipPath={`url(#${id}-b)`}><Glyph ch="S" wght={wght} fill={fill} /></g>
    </>
  );
};

export const SymbolHorizon = ({ className, style }) => (
  <svg viewBox="-60 -700 640 760" className={className} style={style} role="img" aria-label="Selling Sunsets symbol">
    <HorizonS wght={600} />
  </svg>
);

export const WordHorizon = (props) => (
  <WordSVG {...props} renderS={(l, w) => <HorizonS wght={w} gap={14} shift={22} />} />
);

// ---------- CONCEPT B · REFLECTION — S and its inversion across an unseen horizon ----------
export const ReflectionMark = ({ wght = 600, gap = 26 }) => (
  <>
    <Glyph ch="S" wght={wght} />
    <g transform={`translate(0 ${gap}) scale(1 -1)`}><Glyph ch="S" wght={400} opacity="0.55" /></g>
  </>
);
export const SymbolReflection = ({ className, style }) => (
  <svg viewBox="-20 -700 540 1400" className={className} style={style} role="img" aria-label="Selling Sunsets symbol">
    <ReflectionMark />
  </svg>
);

// ---------- CONCEPT C · THRESHOLD — two initials stepped across a rule ----------
export const ThresholdMark = ({ wght = 600 }) => (
  <>
    <Glyph ch="S" wght={wght} x={0} y={0} />
    <rect x="-10" y="36" width="920" height="26" />
    <Glyph ch="S" wght={wght} x={420} y={CAP + 90} />
  </>
);
export const SymbolThreshold = ({ className, style }) => (
  <svg viewBox="-40 -680 980 1460" className={className} style={style} role="img" aria-label="Selling Sunsets symbol">
    <ThresholdMark />
  </svg>
);

// ---------- CONCEPT D · MEANDER — one continuous gesture, two S forms, cut terminals ----------
export const geoS = (r = 150, k = 0.866, t = 0.5) => {
  // two-arc geometric S; terminals cut at 30deg from the axis
  const sx = r * k, sy = r * t;
  return `M ${sx} ${-3 * r + sy} A ${r} ${r} 0 1 0 0 ${-2 * r} A ${r} ${r} 0 1 1 ${-sx} ${-r - sy}`;
};
export const MeanderMark = ({ r = 150, sw = 74 }) => {
  const sx = r * 0.866;
  // second S begins exactly where the first ends: one continuous gesture
  return (
    <>
      <path d={geoS(r)} fill="none" stroke="currentColor" strokeWidth={sw} />
      <path d={geoS(r)} transform={`translate(${-2 * sx} ${r})`} fill="none" stroke="currentColor" strokeWidth={sw} />
    </>
  );
};
export const SymbolMeander = ({ className, style }) => (
  <svg viewBox="-470 -660 680 870" className={className} style={style} role="img" aria-label="Selling Sunsets symbol">
    <MeanderMark />
  </svg>
);

// ---------- CONCEPT E · PARCEL — the S held in a survey frame open at the horizon ----------
export const ParcelMark = ({ wght = 600, sw = 22 }) => {
  const size = 760;
  const x0 = -130;
  const y0 = -705;
  return (
    <>
      <path d={`M ${x0} ${y0 + size} V ${y0} H ${x0 + size} V ${y0 + size}`} fill="none" stroke="currentColor" strokeWidth={sw} />
      <Glyph ch="S" wght={wght} x={0} y={0} />
    </>
  );
};
export const SymbolParcel = ({ className, style }) => (
  <svg viewBox="-200 -780 900 900" className={className} style={style} role="img" aria-label="Selling Sunsets symbol">
    <ParcelMark />
  </svg>
);

// ---------- CONCEPT F · SECTION — two S forms interlocked; a proprietary reference sign ----------
export const SectionMark = ({ wght = 600, bg = "#F7F5F0", overlap = 0.5, knock = 30 }) => {
  const dy = CAP * overlap;
  return (
    <>
      <Glyph ch="S" wght={wght} />
      <g transform={`translate(0 ${dy})`}>
        <Glyph ch="S" wght={wght} fill="none" stroke={bg} strokeWidth={knock} strokeLinejoin="round" />
        <Glyph ch="S" wght={wght} />
      </g>
    </>
  );
};
export const SymbolSection = ({ className, style, bg }) => (
  <svg viewBox="-40 -700 580 1080" className={className} style={style} role="img" aria-label="Selling Sunsets symbol">
    <SectionMark bg={bg} />
  </svg>
);

// ---------- Lockups (symbol + wordmark on one line) ----------
const LOCK_PAD = 40;
export const Lockup = ({ Symbol, symbolViewBox, lockupScale = 1, text = "SELLING SUNSETS", wght = 500, tracking = 70, gap = 140, renderS, className, style, bg }) => {
  const w = wordWidth(text, { wght, tracking });
  const [vx, vy, vw, vh] = symbolViewBox.split(" ").map(Number);
  const scale = lockupScale;
  const sw = vw * scale;
  const sh = vh * scale;
  const total = sw + gap + w;
  const top = Math.min(-CAP - 60, -CAP / 2 - sh / 2 - 40);
  const bottom = Math.max(60, -CAP / 2 + sh / 2 + 40);
  return (
    <svg viewBox={`${-LOCK_PAD} ${top} ${total + LOCK_PAD * 2} ${bottom - top}`} className={className} style={style} role="img" aria-label={text}>
      <g transform={`translate(${-vx * scale} ${-CAP / 2 - (vy + vh / 2) * scale}) scale(${scale})`}>
        <Symbol bg={bg} />
      </g>
      <g transform={`translate(${sw + gap} 0)`}>
        <WordSet text={text} wght={wght} tracking={tracking} renderS={renderS} />
      </g>
    </svg>
  );
};

export const CONCEPTS = [
  {
    key: "horizon",
    lockupScale: 1.15,
    name: "Horizon",
    idea: "A single S divided by a hairline at its optical centre; the upper half slips one increment east. The horizon is never drawn — it is the absence between two halves of a letter.",
    Symbol: () => <HorizonS wght={600} />,
    symbolViewBox: "-60 -700 640 760",
    renderS: (l, w) => <HorizonS wght={w} gap={18} shift={26} />,
    wordTracking: 70,
  },
  {
    key: "reflection",
    lockupScale: 0.6,
    name: "Reflection",
    idea: "The initial and its inversion across an unseen line. Weight falls away in the lower form — the only concession to the name's imagery, carried entirely by typography.",
    Symbol: () => <ReflectionMark />,
    symbolViewBox: "-20 -700 540 1400",
    renderS: null,
    wordTracking: 70,
  },
  {
    key: "threshold",
    lockupScale: 0.55,
    name: "Threshold",
    idea: "Two initials stepped across a rule — one above, one below, offset like adjoining parcels on a survey sheet. Boundary, orientation and the two words of the name in one gesture.",
    Symbol: () => <ThresholdMark />,
    symbolViewBox: "-40 -680 980 1460",
    renderS: null,
    wordTracking: 70,
  },
  {
    key: "meander",
    lockupScale: 0.72,
    name: "Meander",
    idea: "Two S forms drawn as one continuous gesture — a contour line, a river's course, a path through terrain — with horizontally cut terminals. Geometric where the wordmark is calligraphic.",
    Symbol: () => <MeanderMark />,
    symbolViewBox: "-470 -660 680 870",
    renderS: null,
    wordTracking: 70,
  },
  {
    key: "parcel",
    lockupScale: 0.85,
    name: "Parcel",
    idea: "The S held within a survey frame whose lower edge is missing. The letter stands on a horizon that is implied by the frame's absence — a parcel, a threshold, an open boundary.",
    Symbol: () => <ParcelMark />,
    symbolViewBox: "-200 -780 900 900",
    renderS: null,
    wordTracking: 70,
  },
  {
    key: "section",
    lockupScale: 0.72,
    name: "Section",
    idea: "Two S forms interlocked into a proprietary reference sign — the § of land law, the index mark of a research house, and the brand's doubled initial in one continuous device.",
    Symbol: ({ bg }) => <SectionMark bg={bg} />,
    symbolViewBox: "-40 -700 580 1080",
    renderS: null,
    wordTracking: 70,
  },
];
